/**
 * I used this link for referencing how to implement the pagedots
 * https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 * "It is recommended that tabs activate automatically when they
 * receive focus as long as their associated tab panels are displayed
 * without noticeable latency. This typically requires tab panel
 * content to be preloaded. Otherwise, automatic activation slows
 * focus movement, which significantly hampers users' ability
 * to navigate efficiently across the tab list."
 * */

export default class Pagination {
	static pluginName = 'pagination';

	constructor(ctx) {
		const _ = this;
		_.ctx = ctx;
		_.navOptions = {};
		_.paginationContainer = null;
		_.dotsList = null;
		_.isAutoGeneratedNav = false;

		//  debounce render so rapid changes do not rebuild repeatedly
		_.debouncedRender = _.ctx.utils.debounce(() => _.render(), 20);

		_.handlers = {
			//  handle clicks on pagination dots
			paginationClick: (event) => {
				_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'pagination', event });
				const target = event.target.closest('[data-action-tarot-go-to-page]');
				if (!target) return;
				const pageIndex = parseInt(target.getAttribute('data-page-index'), 10);
				_.activateDot(pageIndex);
			},

			//  keyboard navigation with auto-activation
			keyDown: (event) => {
				const allowedKeys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
				if (!allowedKeys.includes(event.key)) return;

				const tabs = Array.from(_.paginationContainer.querySelectorAll('.tarot-dots-button'));
				const currentIndex = tabs.findIndex((tab) => tab.getAttribute('tabIndex') === '0');
				let newIndex = currentIndex;

				const state = _.ctx.store.getState();

				switch (event.key) {
					case 'ArrowRight':
						newIndex = currentIndex + 1;
						if (newIndex >= tabs.length) newIndex = state.canLoop ? 0 : currentIndex;
						break;
					case 'ArrowLeft':
						newIndex = currentIndex - 1;
						if (newIndex < 0) newIndex = state.canLoop ? tabs.length - 1 : currentIndex;
						break;
					case 'Home':
						newIndex = 0;
						break;
					case 'End':
						newIndex = tabs.length - 1;
						break;
					default:
						return;
				}

				event.preventDefault();
				event.stopPropagation();

				//  move focus and auto-activate (proper ARIA pattern for instant content)
				tabs.forEach((tab, i) => {
					tab.tabIndex = i === newIndex ? 0 : -1;
				});
				tabs[newIndex].focus();

				// auto-activate the focused dot
				_.ctx.emitter.emit(_.ctx.events.user.interacted, { via: 'key', event });
				_.activateDot(newIndex);
			},

			optionsChanged: () => {
				_.reInit();
			},

			slidesChanged: () => {
				_.reInit();
			},

			pageChanged: () => {
				_.updateSelectedDot();
			},
		};

		_.init();
	}

	/**
	 * query and setup the pagination container
	 */
	queryDOM() {
		const _ = this;

		//  if a pagination element is provided via selector, use it
		if (_.navOptions && _.navOptions.paginationSelector) {
			const paginationElement = document.querySelector(_.navOptions.paginationSelector);
			if (paginationElement) _.paginationContainer = paginationElement;
		}

		// check for data-tarot-pagination attribute
		if (!_.paginationContainer) {
			const allPaginationContainers = _.ctx.carousel.querySelectorAll('[data-tarot-pagination]');
			_.paginationContainer =
				Array.from(allPaginationContainers).find(
					(el) => el.closest('tarot-carousel') === _.ctx.carousel
				) || null;
		}

		//  if no container provided, generate one
		if (!_.paginationContainer) {
			_.isAutoGeneratedNav = true;
			_.paginationContainer = document.createElement('nav');
			_.paginationContainer.setAttribute('aria-label', 'carousel navigation');
			_.ctx.carousel.appendChild(_.paginationContainer);
		}

		//  clear existing content and set up the container
		_.paginationContainer.innerHTML = '';
		_.paginationContainer.classList.add('tarot-dots-container');

		//  create the <ul> element for dots with role="tablist"
		_.dotsList = document.createElement('ul');
		_.dotsList.classList.add('tarot-dots-list');
		_.dotsList.setAttribute('role', 'tablist');
		_.dotsList.setAttribute('aria-orientation', 'horizontal');
		_.paginationContainer.appendChild(_.dotsList);
	}

	//  initialize the pagination container and build the dots
	init() {
		const _ = this;
		_.navOptions = _.ctx.store.getOptions().navigation || {};
		_.isAutoGeneratedNav = false;

		_.queryDOM();
		_.bindEvents();
		_.render();
	}

	//  reinitialize pagination when carousel options or slides change
	reInit() {
		this.navOptions = this.ctx.store.getOptions().navigation || {};
		this.debouncedRender();
	}

	//  bind carousel events to pagination handlers
	bindEvents() {
		const _ = this;

		const emitter = _.ctx.emitter;
		const events = _.ctx.events.store;

		emitter.on(events.optionsChanged, _.handlers.optionsChanged);
		emitter.on(events.slidesChanged, _.handlers.slidesChanged);
		emitter.on(events.pageIndexChanged, _.handlers.pageChanged);

		//  add event listeners for pagination container events
		_.paginationContainer.addEventListener('click', _.handlers.paginationClick, false);
		_.paginationContainer.addEventListener('keydown', _.handlers.keyDown, false);
	}

	//  build the pagination buttons (dots)
	render() {
		const _ = this;
		const state = _.ctx.store.getState();
		const page = state.pageIndex;
		const pageCount = state.pageCount;
		const carouselID = _.ctx.carousel.id || '';

		_.dotsList.innerHTML = '';

		for (let i = 0; i < pageCount; ++i) {
			const listItem = document.createElement('li');
			listItem.setAttribute('role', 'presentation');

			const button = document.createElement('button');
			button.type = 'button';
			button.classList.add('tarot-dots-button');
			button.setAttribute('role', 'tab');
			button.setAttribute('data-page-index', i);
			button.setAttribute('data-action-tarot-go-to-page', '');
			button.setAttribute('aria-selected', page === i ? 'true' : 'false');
			button.tabIndex = page === i ? 0 : -1;
			button.setAttribute('aria-label', `page ${i + 1}`);
			button.id = `carousel-tab-${i + 1}`;
			if (carouselID) button.setAttribute('aria-controls', carouselID);

			const span = document.createElement('span');
			span.className = 'tarot-visually-hidden';
			span.textContent = `slide ${i + 1}`;

			button.appendChild(span);
			listItem.appendChild(button);
			_.dotsList.appendChild(listItem);
		}

		_.updateSelectedDot();

		if (_.navOptions && _.navOptions.showPagination) _.show();
		else _.hide();
	}

	//  activate a dot and change the carousel page
	activateDot(index) {
		this.ctx.commands.goToPage(index);
		this.updateSelectedDot();
	}

	//  update the selected pagination dot based on the carousel state
	updateSelectedDot() {
		const _ = this;
		const dots = _.paginationContainer.querySelectorAll('.tarot-dots-button');
		const currentPage = `${_.ctx.store.getState().pageIndex}`;
		dots.forEach((dot) => {
			if (dot.dataset.pageIndex === currentPage) {
				dot.setAttribute('aria-selected', 'true');
				dot.tabIndex = 0;
			} else {
				dot.setAttribute('aria-selected', 'false');
				dot.tabIndex = -1;
			}
		});
	}

	//  show the pagination container
	show() {
		this.paginationContainer.style.display = '';
	}

	//  hide the pagination container
	hide() {
		this.paginationContainer.style.display = 'none';
	}

	//  destroy the pagination and unbind everything
	destroy() {
		const _ = this;

		if (_.paginationContainer) {
			_.paginationContainer.removeEventListener('click', _.handlers.paginationClick, false);
			_.paginationContainer.removeEventListener('keydown', _.handlers.keyDown, false);
		}

		_.ctx.emitter.off(_.ctx.events.store.optionsChanged, _.handlers.optionsChanged);
		_.ctx.emitter.off(_.ctx.events.store.slidesChanged, _.handlers.slidesChanged);
		_.ctx.emitter.off(_.ctx.events.store.pageIndexChanged, _.handlers.pageChanged);

		// cancel debounced function if it has a cancel method
		if (_.debouncedRender && typeof _.debouncedRender.cancel === 'function') {
			_.debouncedRender.cancel();
		}

		if (
			_.isAutoGeneratedNav &&
			_.paginationContainer &&
			_.paginationContainer.parentNode === _.ctx.carousel
		) {
			_.ctx.carousel.removeChild(_.paginationContainer);
		}

		_.paginationContainer = null;
		_.dotsList = null;
		_.debouncedRender = null;
	}
}
